import { useMemo } from "react";
import { Card, CardHeader, Flex, Heading, Button, Icon, Icons, CardContent, Row, Division } from "@ir/ui";
import DrawerInfoCol from "./DrawerInfoColumn";
import { Deduction } from "@/core/entities/deduction";
import { useDeductionDrawer, useDeductionEdit } from "@/presentation/deduction/hooks";
import { DeductionInformationLabels } from "./utils/deductionInformationLabel.enum";
import EditDeductionDetails from "./edit/EditDeductionDetails";

import { HasAnyPermission } from "@/presentation/workspace";
import { WorkspacePermissions } from "@/core/permissions";
import { DeductionEditMode } from "@/store/deduction/deduction";
import { useConfirm, useWorkspace } from "@/hooks";
import { DeductionControlAction } from "@/core/entities/deduction/deduction/DeductionControls";

const DeductionInformation = ({ deduction }: { deduction: Deduction }) => {
  const { settings: workspaceSettings } = useWorkspace();
  const { isDeductionsLoading, hasAnyUnsavedChanges, setHasAnyUnsavedChanges } = useDeductionDrawer();
  const { editMode, setEditMode } = useDeductionEdit();
  const confirm = useConfirm();
  const isEditingDetails = useMemo(
    () => !isDeductionsLoading && editMode === DeductionEditMode.DETAILS,
    [editMode, isDeductionsLoading]
  );

  const DeductionDetailsInformation = [
    { label: DeductionInformationLabels.DeductionType, value: deduction?.deductionType },
    { label: DeductionInformationLabels.DeductionSubType, value: deduction?.deductionSubType },
    { label: DeductionInformationLabels.GLCode, value: deduction?.glCode },
    {
      label: DeductionInformationLabels.DeductedAmount,
      value:
        workspaceSettings?.displayNumberFormat && deduction?.amount
          ? "$" + deduction?.amountFormatted(workspaceSettings?.displayNumberFormat)
          : deduction?.amount,
    },
    { label: DeductionInformationLabels.Purchaser, value: deduction?.purchaser },
    {
      label: DeductionInformationLabels.DC,
      value: deduction?.distributionCenterCode
        ? `${deduction?.distributionCenter} (${deduction?.distributionCenterCode})`
        : deduction?.distributionCenter,
    },
    {
      label: DeductionInformationLabels.DeductionDate,
      value: workspaceSettings?.displayDateFormat
        ? deduction?.deductionDateFormatted(workspaceSettings?.displayDateFormat)
        : deduction?.deductionDate,
    },
    {
      label: DeductionInformationLabels.TotalAmount,
      value:
        workspaceSettings?.displayNumberFormat && deduction?.totalAmount
          ? "$" + deduction?.totalAmountFormatted(workspaceSettings?.displayNumberFormat)
          : deduction?.totalAmount,
    },
    { label: DeductionInformationLabels.Period, value: deduction?.period },
    { label: DeductionInformationLabels.PONumber, value: deduction?.poNumber },
    { label: DeductionInformationLabels.Analyst, value: deduction?.analystUserFullName ?? deduction?.analystUserId },
    { label: DeductionInformationLabels.Approver, value: deduction?.approverUserFullName ?? deduction?.approverUserId },
    { label: DeductionInformationLabels.Retailer, value: deduction?.retailerName },
    { label: DeductionInformationLabels.ReasonCode, value: deduction?.reasonCode },
    {
      label: DeductionInformationLabels.Description,
      value: deduction?.description,
      span: 12,
      md: 12,
      fontBold: deduction?.description?.length < 300,
    },
  ];

  const handleEditDeductionDetails = async () => {
    if (editMode === DeductionEditMode.INTELLIGENCE && hasAnyUnsavedChanges) {
      const confirmResult = await confirm({
        title: "Unsaved Changes",
        message: "You have unsaved updates. Leave without saving?",
        confirmText: "Discard",
        cancelText: "Go Back",
      });
      if (confirmResult) {
        setHasAnyUnsavedChanges(false);
        setEditMode(DeductionEditMode.DETAILS);
      } else {
        return;
      }
    } else {
      setEditMode(DeductionEditMode.DETAILS);
    }
  };

  const shouldShowEditButton =
    !isDeductionsLoading &&
    Boolean(deduction?.id) &&
    editMode !== DeductionEditMode.DETAILS &&
    deduction.controls.some(c => c.action === DeductionControlAction.EDIT);

  return (
    <Card className="bg-white mb-3">
      <CardHeader className="py-2">
        <Flex justifyContent="space-between" align="center">
          <Division className="flex">
            <Heading level={6} className="text-grey-700 font-bold m-0">
              Details
            </Heading>
          </Division>
          {shouldShowEditButton && (
            <HasAnyPermission requiredPermissions={[WorkspacePermissions.DEDUCTIONS_EDIT]}>
              <Button size="small" variant="clear" color="grey" onPress={handleEditDeductionDetails}>
                <Icon size="small" name={Icons.Edit} />
              </Button>
            </HasAnyPermission>
          )}
        </Flex>
      </CardHeader>
      <CardContent>
        {isEditingDetails ? (
          <EditDeductionDetails deduction={deduction} />
        ) : (
          <Flex>
            <Row className="flex-1">
              {DeductionDetailsInformation.map(item => (
                <DrawerInfoCol
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  span={item.span}
                  md={item.md}
                  fontBold={item.fontBold}
                  isLoading={isDeductionsLoading}
                  deductionSubTypeHelpText={deduction?.deductionSubTypeHelpText}
                  deduction={deduction}
                />
              ))}
            </Row>
          </Flex>
        )}
      </CardContent>
    </Card>
  );
};

export default DeductionInformation;
